/**
 * GLSL Colormap Implementations
 * Provides colormap functions for shader use
 */

/**
 * Colormap enum for TypeScript
 */
export enum ColorMap {
  VIRIDIS = 0,
  TURBO = 1,
  JET = 2,
  MAGMA = 3,
  INFERNO = 4,
  GRAYSCALE = 5,
}

export const COLOR_MAP_VALUES = [
  ColorMap.VIRIDIS,
  ColorMap.TURBO,
  ColorMap.JET,
  ColorMap.MAGMA,
  ColorMap.INFERNO,
  ColorMap.GRAYSCALE,
] as const;

export const COLOR_MAP_LABELS: Record<ColorMap, string> = {
  [ColorMap.VIRIDIS]: "Viridis",
  [ColorMap.TURBO]: "Turbo",
  [ColorMap.JET]: "Jet",
  [ColorMap.MAGMA]: "Magma",
  [ColorMap.INFERNO]: "Inferno",
  [ColorMap.GRAYSCALE]: "Grayscale",
};

export function getColorMapLabel(colorMap: ColorMap): string {
  return COLOR_MAP_LABELS[colorMap];
}

/**
 * Turbo colormap GLSL implementation
 * A perceptually uniform colormap designed by Google AI
 */
const turboColormap = `
  vec3 turboColor(float t) {
  float x = clamp(t, 0.0, 1.0);
  vec4 v4 = vec4(1.0, x, x*x, x*x*x);
  vec2 v2 = v4.zw * v4.z; // x^4, x^5

  const vec4 kRedVec4   = vec4(0.13572138,  4.61539260, -42.66032258, 132.13108234);
  const vec4 kGreenVec4 = vec4(0.09140261,  2.19418839,   4.84296658, -14.18503333);
  const vec4 kBlueVec4  = vec4(0.10667330, 12.64194608, -60.58204836, 110.36276771);

  const vec2 kRedVec2   = vec2(-152.94239396, 59.28637943);
  const vec2 kGreenVec2 = vec2(   4.27729857,  2.82956604);
  const vec2 kBlueVec2  = vec2( -89.90310912, 27.34824973);

  vec3 rgb = vec3(
    dot(v4, kRedVec4)   + dot(v2, kRedVec2),
    dot(v4, kGreenVec4) + dot(v2, kGreenVec2),
    dot(v4, kBlueVec4)  + dot(v2, kBlueVec2)
  );

  return clamp(rgb, 0.0, 1.0);
}

`;

/**
 * Jet colormap GLSL implementation
 * Classic MATLAB jet colormap
 */
const jetColormap = `
  vec3 jetColor(float x) {
    float r = clamp(min(4.0*x - 1.5, -4.0*x + 4.5), 0.0, 1.0);
    float g = clamp(min(4.0*x - 0.5, -4.0*x + 3.5), 0.0, 1.0);
    float b = clamp(min(4.0*x + 0.5, -4.0*x + 2.5), 0.0, 1.0);
    return vec3(r, g, b);
  }
`;

/**
 * Viridis colormap GLSL implementation
 * 6th-order Horner polynomial fit to the 256-stop matplotlib viridis data (max error ~4/255)
 */
const viridisColormap = `
  vec3 viridisColor(float t) {
    float x = clamp(t, 0.0, 1.0);

    const vec3 c0 = vec3( 0.2777273272234177,   0.005407344544966578,  0.3340998053353061);
    const vec3 c1 = vec3( 0.1050930431085774,   1.404613529898575,     1.384590162594685);
    const vec3 c2 = vec3(-0.3308618287255563,   0.214847559468213,     0.09509516302823659);
    const vec3 c3 = vec3(-4.634230498983486,   -5.799100973351585,   -19.33244095627987);
    const vec3 c4 = vec3( 6.228269936347081,   14.17993336680509,     56.69055260973585);
    const vec3 c5 = vec3( 4.776384997670288,  -13.74514537774601,    -65.35303263337234);
    const vec3 c6 = vec3(-5.435455855934631,    4.645852612178535,    26.3124352495832);

    return clamp(c0 + x*(c1 + x*(c2 + x*(c3 + x*(c4 + x*(c5 + x*c6))))), 0.0, 1.0);
  }
`;

/**
 * Magma colormap GLSL implementation (matplotlib magma)
 * 6th-order polynomial approximation (Horner form)
 */
const magmaColormap = `
  vec3 magmaColor(float t) {
    float x = clamp(t, 0.0, 1.0);

    const vec3 c0 = vec3(-0.002136485053939582, -0.000749655052795221, -0.005386127855323933);
    const vec3 c1 = vec3( 0.2516605407371642,   0.6775232436837668,   2.494026599312351);
    const vec3 c2 = vec3( 8.353717279216625,   -3.577719514958484,    0.3144679030132573);
    const vec3 c3 = vec3(-27.66873308576866,   14.26473078053022,   -13.64921318813922);
    const vec3 c4 = vec3( 52.17613981234068,  -27.94360607168351,    12.94416944238394);
    const vec3 c5 = vec3(-50.76852536473588,   29.04658282127291,     4.23415299384598);
    const vec3 c6 = vec3( 18.65570506591883,  -11.48977351997711,    -5.601961508734096);

    // Horner evaluation
    vec3 rgb = c0 + x*(c1 + x*(c2 + x*(c3 + x*(c4 + x*(c5 + x*c6)))));

    // safety clamp (fit can slightly overshoot)
    return clamp(rgb, 0.0, 1.0);
  }
`;

/**
 * Inferno colormap GLSL implementation (matplotlib inferno)
 * 6th-order polynomial approximation (Horner form)
 */
const infernoColormap = `
  vec3 infernoColor(float t) {
    float x = clamp(t, 0.0, 1.0);

    const vec3 c0 = vec3( 0.0002189403691192265,  0.001651004631001012, -0.01948089843709184);
    const vec3 c1 = vec3( 0.1065134194856116,     0.5639564367884091,   3.932712388889277);
    const vec3 c2 = vec3(11.60249308247187,     -3.972853965665698,   -15.9423941062914);
    const vec3 c3 = vec3(-41.70399613139459,     17.43639888205313,    44.35414519872813);
    const vec3 c4 = vec3(77.162935699427,       -33.40235894210092,   -58.727762559878);
    const vec3 c5 = vec3(-71.31942824499214,     32.62606426397723,    31.92234448290774);
    const vec3 c6 = vec3(25.13112622477341,     -12.24266895238567,   -5.798600375185062);

    // Horner evaluation
    vec3 rgb = c0 + x*(c1 + x*(c2 + x*(c3 + x*(c4 + x*(c5 + x*c6)))));

    // safety clamp (fit can slightly overshoot)
    return clamp(rgb, 0.0, 1.0);
  }
`;

/**
 * Grayscale colormap GLSL implementation
 * Simple linear grayscale
 */
const grayscaleColormap = `
  vec3 grayscaleColor(float t) {
    float x = clamp(t, 0.0, 1.0);
    return vec3(x, x, x);
  }
`;

/**
 * Combined colormaps GLSL code
 * Includes all colormap implementations
 */
export const colorMaps = `
  ${turboColormap}
  ${jetColormap}
  ${viridisColormap}
  ${magmaColormap}
  ${infernoColormap}
  ${grayscaleColormap}
`;
